import { apiClient } from "./apiClient";

export interface UsuarioRanking {
  usuarioId: number;
  nome: string;
  quantidadePizzas: number;
  tempoMedio: string;
}

export interface RankingResponse {
  ranking: UsuarioRanking[];
}

export interface RankingFilters {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
  usuarioId?: number | null;
}

export interface RankingDiaFilters {
  lojaId?: number | null;
}

export interface RankingDiaResponse {
  quantidadeTotal: number;
  quantidadeEmProducao: number;
  tempoMedioGeral: string;
  ranking: UsuarioRanking[];
}

export interface RankingDiaMontagemResponse extends RankingDiaResponse {
  pendentesFila: number;
  quantidadeEmMontagem: number;
}

export interface ProducaoResumo {
  totalProducoesFinalizadas: number;
  totalProducoesTodas?: number;
  totalProducoesCardapioWeb?: number;
  totalPedidosCardapioWebDelivery?: number;
  totalPedidosCardapioWebBalcao?: number;
  totalProducoesSalao?: number;
  totalProducoesExternas?: number;
  totalProducoesRodizio?: number;
  totalPorcoesSalao?: number;
  totalBebidasSalao?: number;
  quantidadeUsuarios: number;
  somaTempoTotal: number;
  totalProducoesEmAndamento: number;
  usuarioMenorTempo: string;
  tempoMedio: string;
}

export interface UsuarioProducao {
  usuarioId: number;
  nome: string;
  roleDescricao: string;
  somaTempoTotal: number;
  tempoMedio: string;
  quantidadeProducoes: number;
}

export interface ProducaoUsuariosResponse {
  usuarios: UsuarioProducao[];
}

export interface ProducaoResumoFilters {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
}

export interface PedidoOrigemItem {
  origem: "cardapio" | "salao" | "balcao";
  label: string;
  quantidade: number;
}

export interface PizzaTamanhoOrigemItem {
  tamanhoNome: string;
  cardapio: number;
  salao: number;
}

export interface DashboardOrigemPizzas {
  pedidosPorOrigem: PedidoOrigemItem[];
  totalPedidos: number;
  pizzasPorTamanhoOrigem: PizzaTamanhoOrigemItem[];
  totalPizzas: number;
}

export interface TamanhoProducao {
  tamanho: string;
  quantidade: number;
}

export interface BordaProducao {
  bordaId: number | null;
  borda: string;
  quantidadeTotal: number;
  tamanhos: Array<{
    tamanho: string;
    quantidade: number;
  }>;
}

export interface RelatorioSaboresProducaoFilters {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
  tamanhoId?: number | null;
  partes?: number | null;
  saborIds?: number[];
  usuarioId?: number | null;
}

export interface RelatorioSaboresProducaoItem {
  lojaId: number;
  lojaNome: string;
  saborId: number;
  saborNome: string;
  tamanhoId: number;
  tamanhoNome: string;
  partes: number;
  quantidade: number;
  rotulo: string;
}

/** Tamanho de página das tabelas do relatório (alinhado ao backend). */
export const RELATORIO_SABORES_PAGE_SIZE = 50;

/** Máximo por página aceito pelo backend em relatórios paginados. */
const RELATORIO_SABORES_FETCH_ALL_PAGE_SIZE = 100;

export interface RelatorioSaboresDetalhadoPaginatedResponse {
  data: RelatorioSaboresProducaoItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  totalQuantidadeGeral: number;
  totalQuantidadePagina: number;
}

export interface RelatorioSaboresAgregadoLojaTamanhoSaborRow {
  lojaNome: string;
  tamanhoNome: string;
  saborNome: string;
  quantidade: number;
}

export interface RelatorioSaboresAgregadoPaginatedResponse {
  data: RelatorioSaboresAgregadoLojaTamanhoSaborRow[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  totalQuantidadeFracionadaGeral: number;
  totalQuantidadeFracionadaPagina: number;
}

function appendRelatorioSaboresQueryParams(
  params: URLSearchParams,
  filters: RelatorioSaboresProducaoFilters
): void {
  params.append("dataInicio", filters.dataInicio);
  params.append("dataFim", filters.dataFim);
  if (filters.lojaId != null && filters.lojaId !== undefined) {
    params.append("lojaId", filters.lojaId.toString());
  }
  if (filters.tamanhoId != null && filters.tamanhoId !== undefined) {
    params.append("tamanhoId", filters.tamanhoId.toString());
  }
  if (filters.partes != null && filters.partes !== undefined) {
    params.append("partes", filters.partes.toString());
  }
  if (filters.saborIds != null && filters.saborIds.length > 0) {
    params.set("saborIds", filters.saborIds.join(","));
  }
  if (filters.usuarioId != null && filters.usuarioId !== undefined) {
    params.append("usuarioId", filters.usuarioId.toString());
  }
}

export interface Producao {
  id: number;
  dataInicio: string;
  dataFim: string | null;
  tempoTotal: number | null;
  orderId?: number | null;
  saboresIds?: number[] | null;
  loja: {
    id: number;
    nome: string;
  };
  usuario: {
    id: number;
    nome: string;
  };
  borda: {
    id: number;
    nome: string;
  };
  tamanho: {
    id: number;
    nome: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ProducaoFilters {
  dataInicio?: string;
  dataFim?: string;
  lojaId?: number | null;
  usuarioId?: number | null;
  bordaId?: number | null;
  tamanhoId?: number | null;
  page?: number;
  limit?: number;
}

export interface PaginatedProducaoResponse {
  data: Producao[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const producaoService = {
  getAll: async (
    filters: ProducaoFilters
  ): Promise<PaginatedProducaoResponse> => {
    const params = new URLSearchParams();
    if (filters.dataInicio) {
      params.append("dataInicio", filters.dataInicio);
    }
    if (filters.dataFim) {
      params.append("dataFim", filters.dataFim);
    }
    if (filters.lojaId !== null && filters.lojaId !== undefined) {
      params.append("lojaId", filters.lojaId.toString());
    }
    if (filters.usuarioId !== null && filters.usuarioId !== undefined) {
      params.append("usuarioId", filters.usuarioId.toString());
    }
    if (filters.bordaId !== null && filters.bordaId !== undefined) {
      params.append("bordaId", filters.bordaId.toString());
    }
    if (filters.tamanhoId !== null && filters.tamanhoId !== undefined) {
      params.append("tamanhoId", filters.tamanhoId.toString());
    }
    if (filters.page) {
      params.append("page", filters.page.toString());
    }
    if (filters.limit) {
      params.append("limit", filters.limit.toString());
    }

    return apiClient.get(`/producoes?${params.toString()}`);
  },
  getResumo: async (
    filters: ProducaoResumoFilters
  ): Promise<ProducaoResumo> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/producoes/resumo?${params.toString()}`);
  },

  getDashboardOrigemPizzas: async (
    filters: ProducaoResumoFilters
  ): Promise<DashboardOrigemPizzas> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(
      `/producoes/dashboard-origem-pizzas?${params.toString()}`
    );
  },

  getResumoMontagem: async (
    filters: ProducaoResumoFilters
  ): Promise<ProducaoResumo> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/producoes/resumo-montagem?${params.toString()}`);
  },

  getUsuarios: async (
    filters: ProducaoResumoFilters
  ): Promise<ProducaoUsuariosResponse> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/producoes/periodo?${params.toString()}`);
  },

  getUsuariosMontagem: async (
    filters: ProducaoResumoFilters
  ): Promise<ProducaoUsuariosResponse> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/producoes/periodo-montagem?${params.toString()}`);
  },

  getRanking: async (filters: RankingFilters): Promise<RankingResponse> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }
    if (filters.usuarioId) {
      params.append("usuarioId", filters.usuarioId.toString());
    }

    return apiClient.get(`/producoes/ranking?${params.toString()}`);
  },

  getRankingDia: async (
    filters?: RankingDiaFilters
  ): Promise<RankingDiaResponse> => {
    const params = new URLSearchParams();
    if (filters?.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }
    const qs = params.toString();
    return apiClient.get(`/producoes/ranking-dia${qs ? `?${qs}` : ""}`);
  },

  getRankingDiaMontagem: async (
    filters?: RankingDiaFilters
  ): Promise<RankingDiaMontagemResponse> => {
    const params = new URLSearchParams();
    if (filters?.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }
    const qs = params.toString();
    return apiClient.get(
      `/producoes/ranking-dia-montagem${qs ? `?${qs}` : ""}`
    );
  },

  getTamanhos: async (
    filters: ProducaoResumoFilters
  ): Promise<TamanhoProducao[]> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/producoes/tamanho?${params.toString()}`);
  },

  getBordas: async (
    filters: ProducaoResumoFilters
  ): Promise<BordaProducao[]> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/producoes/borda?${params.toString()}`);
  },

  getRelatorioSabores: async (
    filters: RelatorioSaboresProducaoFilters
  ): Promise<RelatorioSaboresProducaoItem[]> => {
    const linhas: RelatorioSaboresProducaoItem[] = [];
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const res = await producaoService.getRelatorioSaboresDetalhadoPaginado(
        filters,
        page,
        RELATORIO_SABORES_FETCH_ALL_PAGE_SIZE
      );
      linhas.push(...res.data);
      totalPages = res.totalPages;
      page += 1;
    }

    return linhas;
  },

  getRelatorioSaboresDetalhadoPaginado: async (
    filters: RelatorioSaboresProducaoFilters,
    page: number,
    limit: number = RELATORIO_SABORES_PAGE_SIZE
  ): Promise<RelatorioSaboresDetalhadoPaginatedResponse> => {
    const params = new URLSearchParams();
    appendRelatorioSaboresQueryParams(params, filters);
    params.append("page", String(page));
    params.append("limit", String(limit));
    return apiClient.get(
      `/producoes/relatorio-sabores/detalhado-paginado?${params.toString()}`
    );
  },

  getRelatorioSaboresAgregadoLojaTamanhoSaborPaginado: async (
    filters: RelatorioSaboresProducaoFilters,
    page: number,
    limit: number = RELATORIO_SABORES_PAGE_SIZE
  ): Promise<RelatorioSaboresAgregadoPaginatedResponse> => {
    const params = new URLSearchParams();
    appendRelatorioSaboresQueryParams(params, filters);
    params.append("page", String(page));
    params.append("limit", String(limit));
    return apiClient.get(
      `/producoes/relatorio-sabores/agregado-loja-tamanho-sabor-paginado?${params.toString()}`
    );
  },

  create: async (data: {
    lojaId: number;
    usuarioId: number;
    bordaId: number;
    tamanhoId: number;
  }): Promise<Producao> => {
    return apiClient.post("/producoes", data);
  },

  finalizar: async (id: number): Promise<Producao> => {
    return apiClient.put(`/producoes/${id}/finalizar`, {});
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/producoes/${id}`);
  },

  getProducaoEmAndamento: async (
    usuarioId: number
  ): Promise<Producao | null> => {
    const params = new URLSearchParams();
    params.append("usuarioId", usuarioId.toString());
    params.append("page", "1");
    params.append("limit", "10");

    const response: PaginatedProducaoResponse = await apiClient.get(
      `/producoes?${params.toString()}`
    );

    const producaoEmAndamento = response.data.find((p) => !p.dataFim);
    return producaoEmAndamento || null;
  },
};
